// Library methods
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router";

// MUI Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

// Components
import Loader from "./../Loader";
import DefaultModal from "../../../components/UI/DefaultModal";
import LiveUpdateNavigationModal from "./LiveUpdateNavigationModal";
import LiveUpdateActions from "./LiveUpdateActions";
import LiveUpdateControls from "./LiveUpdateControls";
import LiveUpdateStepper from "./LiveUpdateStepper";
import LiveUpdateGraph from "./LiveUpdateGraph";
import LiveUpdateAlerts from "./LiveUpdateAlerts";
import LiveUpdatePackageInfo from "./LiveUpdatePackageInfo";

// Utilities
import {
  subscribeToDevice,
  unsubscribeFromDevice,
} from "../../../services/SignalR";
import { getExamSectionText } from "../../../utils/examReportsHelper";

// Contexts
import { useLiveUpdateStatusContext } from "../../../contexts/LiveUpdateStatusContext";

const LiveUpdateModal = ({
  connection,
  refetchDevices,
  refetchExams,
  isLoadingSignalR,
}) => {
  // auth
  const { getAccessTokenSilently } = useAuth0();

  // translations
  const { t } = useTranslation();

  // navigation
  const navigate = useNavigate();

  const {
    exam,
    open,
    device,
    examStatus,
    isVrMode,
    shouldDisplayNavigation,
    fullScreenModal,
    peerConnection,
    reportUrl,
    actions,
  } = useLiveUpdateStatusContext();

  const {
    stopWebRTC,
    setShouldDisplayNavigation,
    setExamReportLive,
    setIsRightCompleted,
    setIsTerminated,
    setIsNotStarted,
    setIsPaused,
    setIsConfirmation,
    resetTasks,
    setOpen,
  } = actions;

  // modal fullScreen on small screens
  const theme = useTheme();
  const fullScreen =
    useMediaQuery(theme.breakpoints.down("sm")) || fullScreenModal;

  const patientName = useMemo(
    () =>
      exam?.patient?.firstName && exam?.patient?.lastName
        ? `${exam.patient.firstName} ${exam.patient.lastName}`
        : "",
    [exam?.patient]
  );

  const isMultiExamPackage = useMemo(
    () => exam?.sectionTypeOrder?.length > 1,
    [exam?.sectionTypeOrder]
  );

  // get description for the currently-tested exam
  const currentExamDescription = useMemo(
    () => getExamSectionText(exam?.nextSection, t),
    [exam?.nextSection, t]
  );

  // handle the device subscription
  const handleSubscribeToDevice = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await subscribeToDevice(token, device?.id, connection);

      const data = JSON.parse(response);
      if (data?.state === true) {
        // sus
        connection?.off("DeviceLiveDataUpdated");
        connection.on("DeviceLiveDataUpdated", function (data) {
          const parseableString = data
            .replace(/\\/g, "")
            .replace(/"{/g, "{")
            .replace(/}"/g, "}");

          const deviceLiveData = JSON.parse(parseableString);
          setExamReportLive(deviceLiveData.data);
        });

        // exam completion listeners
        connection?.off("DeviceExamSubmitted");
        connection.on("DeviceExamSubmitted", function () {
          setShouldDisplayNavigation(true);
          setOpen(false);
        });
      }
    } catch (e) {
      console.error("Error while subscribing:", e.message);
    }
  }, [
    connection,
    device?.id,
    getAccessTokenSilently,
    setExamReportLive,
    setShouldDisplayNavigation,
    setOpen,
  ]);

  // handle the device unsubscription
  const handleUnsubscribeFromDevice = useCallback(async () => {
    try {
      const response = await unsubscribeFromDevice(device?.id, connection);
      const data = JSON.parse(response);
      if (data?.state === true) {
        setExamReportLive(undefined);

        // remove listeners
        connection?.off("DeviceLiveDataUpdated");
        connection?.off("DeviceExamSubmitted");
      }
    } catch (e) {
      console.error("Could not unsubscribe from device:", e.message);
    }
  }, [connection, device?.id, setExamReportLive]);

  const handleCloseModal = useCallback(
    (event, reason) => {
      const isAlternativeQuit =
        reason === "escapeKeyDown" || reason === "backdropClick";

      // not performing the on close when not quitting through the quit button when on vr mode
      if (isAlternativeQuit) return;
      if (peerConnection) stopWebRTC();
      resetTasks();
      setOpen(false);
    },
    [resetTasks, setOpen, stopWebRTC, peerConnection]
  );

  const handleNavigate = useCallback(
    (event, reason) => {
      handleCloseModal(event, reason);
      navigate(reportUrl);
    },
    [handleCloseModal, navigate, reportUrl]
  );

  useEffect(() => {
    if (connection && device?.id && exam && !isLoadingSignalR) {
      if (open) {
        handleSubscribeToDevice();
      } else {
        handleUnsubscribeFromDevice();
      }
    }
  }, [
    connection,
    open,
    device?.id,
    exam,
    isLoadingSignalR,
    handleSubscribeToDevice,
    handleUnsubscribeFromDevice,
  ]);

  useEffect(() => {
    const controller = new AbortController();
    if (!isLoadingSignalR) {
      refetchDevices(controller.signal);
      if (open) {
        refetchExams(controller.signal);
      }
    }
    return () => {
      controller.abort();
    };
  }, [isLoadingSignalR, open, refetchDevices, refetchExams]);

  useEffect(() => {
    if (!open) return;
    setIsNotStarted(examStatus?.status === t("word_not_started"));
    setIsPaused(examStatus?.status === t("word_pause"));
    setIsConfirmation(examStatus?.status === t("word_confirmation"));
    setIsRightCompleted(examStatus?.status === t("word_right_eye_completed"));
    setIsTerminated(examStatus?.status === t("word_terminated"));
  }, [
    examStatus?.status,
    t,
    open,
    setIsConfirmation,
    setIsNotStarted,
    setIsPaused,
    setIsRightCompleted,
    setIsTerminated,
  ]);

  useEffect(() => {
    if (!isVrMode && peerConnection) stopWebRTC();
  }, [isVrMode, peerConnection, stopWebRTC]);

  return (
    <>
      <DefaultModal
        isOpen={open}
        onClose={handleCloseModal}
        fullScreen={fullScreen}
        maxWidth="md"
        title={t("tooltip_live_update_panel")}
        modalActions={
          <LiveUpdateControls
            isLoadingSignalR={isLoadingSignalR}
            connection={connection}
          />
        }
      >
        <Grid p="20px">
          {isLoadingSignalR && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              p="20px"
              width="fit-content"
              backgroundColor="#202338"
              borderRadius="8px"
              gap="16px"
              sx={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 100,
              }}
            >
              <Loader size="24px" containerHeight="24px" />
              <Typography color="#FFF" fontSize="20px" fontWeight={600}>
                {t("live_update_reconnecting")}
              </Typography>
            </Box>
          )}

          <Grid
            display="flex"
            flexDirection="column"
            gap="16px"
            sx={{ opacity: isLoadingSignalR ? 0.4 : 1 }}
          >
            {/* Modal action buttons and volume slider */}
            <LiveUpdateActions connection={connection} />
            {/* Error alerts */}
            <LiveUpdateAlerts />

            <Grid
              display="flex"
              flexDirection={{
                xs: "column",
                md: isMultiExamPackage ? "row" : "column",
              }}
              gap="16px"
              width="100%"
            >
              <Grid
                display="flex"
                flexDirection="column"
                gap="16px"
                width={{
                  xs: "100%",
                  md: isMultiExamPackage ? "40%" : "100%",
                }}
              >
                {/* Patient information */}
                <Box display="flex" flexDirection="column" gap="4px">
                  <Typography fontWeight="bold" fontSize="18px">
                    {patientName}
                  </Typography>
                  {isMultiExamPackage && (
                    <Typography fontSize="16px" color="#5B6D7A">
                      {t("word_multi_exam_package")}
                    </Typography>
                  )}
                </Box>

                {/* Multi-package information */}
                {isMultiExamPackage && <LiveUpdatePackageInfo />}
              </Grid>

              <Grid
                display="flex"
                flexDirection="column"
                gap="16px"
                width={{
                  xs: "100%",
                  md: isMultiExamPackage ? "60%" : "100%",
                }}
              >
                {/* Individual exam information */}
                <Box display="flex" flexDirection="column" gap="4px">
                  <Typography fontWeight="bold" fontSize="18px">
                    {t("word_exam_visualization")}
                  </Typography>
                  <Typography fontSize="16px" color="#5B6D7A">
                    {currentExamDescription}
                  </Typography>
                </Box>

                {/* Exam steps and percentage */}
                <LiveUpdateStepper />

                {/* Exam graphs */}
                <LiveUpdateGraph connection={connection} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DefaultModal>
      {shouldDisplayNavigation && (
        <LiveUpdateNavigationModal
          isOpen={shouldDisplayNavigation}
          onClose={handleCloseModal}
          onConfirm={handleNavigate}
        />
      )}
    </>
  );
};

export default LiveUpdateModal;
